import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import CustomButton from "@/components/common/Button";
import { ProColumns } from "@ant-design/pro-components";
import {
  Edit2,
  Users,
  Upload,
  User,
  Inbox,
  CheckCircle,
  XCircle,
} from "lucide-react";

import MemberService from "@/services/APIs/MemberService";
import { Member } from "@/utils/types/member";
import { BaseTable } from "@/components/BaseTable";
import MMNavigationRoutes from "../../utils/navigationRoutes";
import { Badge } from "@/projects/Civi_Circle_Admin/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/projects/Civi_Circle_Admin/components/ui/tabs";
import { AddMemberModal } from "@/projects/Civi_Circle_Admin/components/members/AddMemberModal";
import { RosterManagementTab } from "@/projects/Civi_Circle_Admin/components/membership/RosterManagementTab";
import { MemberInquiriesTab } from "@/projects/Civi_Circle_Admin/components/members/MemberInquiriesTab";

const MemberListPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(searchParams.get("pageSize") ?? "10", 10);
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);
  const [searchText, setSearchText] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("profiles");

  // Query for User Profiles tab (member_status=true)
  const {
    data: profilesData,
    isLoading: isLoadingProfiles,
    refetch: refetchProfiles,
  } = useQuery({
    queryKey: ["members", "profiles", page, pageSize],
    queryFn: () =>
      MemberService.getAllMembers({ page, pageSize, member_status: true }),
    enabled: activeTab === "profiles",
  });

  // Query for Member Inquiries tab (member_status=false)
  const {
    data: inquiriesData,
    isLoading: isLoadingInquiries,
    refetch: refetchInquiries,
  } = useQuery({
    queryKey: ["members", "inquiries", page, pageSize],
    queryFn: () =>
      MemberService.getAllMembers({ page, pageSize, member_status: false }),
    enabled: activeTab === "inquiries",
  });

  // Use appropriate data based on active tab
  const data = activeTab === "profiles" ? profilesData : inquiriesData;
  const isLoading =
    activeTab === "profiles" ? isLoadingProfiles : isLoadingInquiries;
  const refetch = activeTab === "profiles" ? refetchProfiles : refetchInquiries;

  const handleEdit = (record: Member) => {
    console.log("Organization ID:", record.organization_id);
    navigate(
      `/mm${MMNavigationRoutes.EDIT_MEMBER(record.member_id)}?organization_id=${record.organization_id}`
    );
  };

  const getInitials = (firstName?: string | null, lastName?: string | null) => {
    const first = firstName?.charAt(0)?.toUpperCase() ?? "";
    const last = lastName?.charAt(0)?.toUpperCase() ?? "";
    return `${first}${last}` || "?";
  };

  const total = data?.count ?? 0;

  // Filter data based on search text
  const filteredData = useMemo(() => {
    const gridData = data?.results ?? [];
    if (!searchText.trim()) {
      return gridData;
    }
    const searchLower = searchText.toLowerCase();
    return gridData.filter((member: any) => {
      const fullName =
        `${member.member_first_name ?? ""} ${member.member_last_name ?? ""}`.toLowerCase();
      const email = (member.member_email ?? "").toLowerCase();
      const organization = (member.organization_name ?? "").toLowerCase();
      const department = (member.department ?? "").toLowerCase();
      const position = (member.position ?? "").toLowerCase();
      const phone = (
        member.member_phone_number ??
        member.member_phone ??
        ""
      ).toLowerCase();

      return (
        (fullName.includes(searchLower) || email.includes(searchLower)) ??
        organization.includes(searchLower) ??
        department.includes(searchLower) ??
        position.includes(searchLower) ??
        phone.includes(searchLower)
      );
    });
  }, [data?.results, searchText]);

  const columns: ProColumns<Member>[] = [
    {
      title: "Member",
      dataIndex: "name",
      key: "name",
      render: (_text: any, record) => {
        const fullName =
          `${record.member_first_name ?? ""} ${record.member_last_name ?? ""}`.trim() ||
          "-";
        const initials = getInitials(
          record.member_first_name,
          record.member_last_name
        );
        const email = record.member_email ?? "";

        return (
          <Link
            to={`/mm${MMNavigationRoutes.EDIT_MEMBER(record.member_id)}?organization_id=${record.organization_id}`}
            className="flex items-center gap-3"
          >
            <div className="h-10 w-10 flex-shrink-0">
              <div className="flex h-full w-full items-center justify-center rounded-full bg-primary/10 text-primary">
                <span className="text-sm font-semibold">{initials}</span>
              </div>
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-foreground">{fullName}</span>
              {email && (
                <span className="text-sm text-muted-foreground">{email}</span>
              )}
            </div>
          </Link>
        );
      },
    },
    {
      title: "Organization",
      dataIndex: "organization_name",
      key: "organization_name",
    },
    {
      title: "Department",
      dataIndex: "department",
      key: "department",
    },
    {
      title: "Position",
      dataIndex: "position",
      key: "position",
    },
    {
      title: "Skills",
      dataIndex: "skillinterests",
      key: "skillinterests",
      render: (_text: any, record: any) => {
        const skills = record.skillinterests;

        if (!skills || (Array.isArray(skills) && skills.length === 0)) {
          return <span className="text-muted-foreground">-</span>;
        }

        const skillsArray = Array.isArray(skills) ? skills : [skills];

        return (
          <div className="flex flex-wrap gap-1">
            {skillsArray.map((skill: string) => (
              <Badge
                key={`${record.id ?? record.member_id ?? ""}-${skill}`}
                variant="secondary"
                className="text-xs"
              >
                {skill}
              </Badge>
            ))}
          </div>
        );
      },
    },
    {
      title: "Verification",
      dataIndex: "member_verified",
      key: "member_verified",
      render: (_text: any, record: any) => {
        const verifiedValue = record.member_verified;
        const isVerified =
          verifiedValue === true ||
          verifiedValue === "true" ||
          verifiedValue === "True" ||
          verifiedValue === 1 ||
          verifiedValue === "1" ||
          (typeof verifiedValue === "string" &&
            verifiedValue.toLowerCase() === "yes");

        return (
          <div className="flex items-center gap-2">
            {isVerified ? (
              <>
                <CheckCircle className="text-success h-4 w-4" />
                <span className="text-sm text-foreground">Verified</span>
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Not Verified
                </span>
              </>
            )}
          </div>
        );
      },
    },
    {
      title: "Phone Number",
      dataIndex: "member_phone_number",
      key: "member_phone_number",
    },
    {
      title: "Status",
      dataIndex: "member_status",
      key: "member_status",
      render: (_text: any, record: Member) => {
        const status = record.member_status ?? "Active";
        const statusLower = String(status).toLowerCase();

        // Determine badge color based on status (theme-aware)
        let bgColor = "bg-success";
        let textColor = "text-success-foreground";
        if (statusLower === "inactive" || statusLower === "in-active") {
          bgColor = "bg-muted";
          textColor = "text-muted-foreground";
        } else if (statusLower === "pending") {
          bgColor = "bg-warning";
          textColor = "text-warning-foreground";
        } else if (statusLower === "suspended") {
          bgColor = "bg-destructive";
          textColor = "text-destructive-foreground";
        }
        return (
          <div
            className={`inline-flex items-center rounded-full border border-transparent ${bgColor} ${textColor} px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2`}
          >
            {status}
          </div>
        );
      },
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() => handleEdit(record)}
          type="text"
          title="Edit Member"
        />,
      ],
    },
  ];

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Users className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Members</h1>
            <p className="text-muted-foreground">
              Manage roster and user profiles
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="space-y-4"
        >
          <TabsList className="grid w-full grid-cols-3 lg:inline-grid lg:w-auto">
            <TabsTrigger value="profiles" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              User Profiles
            </TabsTrigger>
            <TabsTrigger value="inquiries" className="flex items-center gap-2">
              <Inbox className="h-4 w-4" />
              Member Inquiries
            </TabsTrigger>
            <TabsTrigger value="roster" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Roster
            </TabsTrigger>
          </TabsList>

          {/* User Profiles Tab */}
          <TabsContent value="profiles" className="space-y-4">
            {/* Search Bar Section */}
            <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
              <div className="p-6 pt-6">
                <div className="flex gap-4">
                  <div className="relative flex-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-search absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.3-4.3" />
                    </svg>
                    <input
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pl-10 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                      placeholder="Search members..."
                      value={searchText}
                      onChange={e => setSearchText(e.target.value)}
                    />
                  </div>
                  <button
                    className="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                    onClick={() => {
                      console.log("Filters clicked");
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-filter mr-2 h-4 w-4"
                    >
                      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                    </svg>
                    Filters
                  </button>
                </div>
              </div>
            </div>

            {/* Table */}
            <BaseTable<Member>
              loading={isLoading}
              columns={columns}
              dataSource={filteredData}
              page={page}
              pageSize={pageSize}
              total={searchText.trim() ? filteredData.length : total}
              rowKey="member_id"
              toolBarRender={() => []}
              headerTitle="Member List"
              onPageChange={(page, size) => {
                setPage(page);
                setPageSize(size ?? 10);
                searchParams.set("page", page.toString());
                searchParams.set("pageSize", size.toString());
                navigate(
                  `/mm${MMNavigationRoutes.MEMBER_LIST}?${searchParams.toString()}`
                );
              }}
              request={() => refetch()}
            />
          </TabsContent>

          {/* Member Inquiries Tab */}
          <TabsContent value="inquiries">
            <MemberInquiriesTab />
          </TabsContent>

          {/* Roster Tab */}
          <TabsContent value="roster">
            <RosterManagementTab />
          </TabsContent>
        </Tabs>
      </div>

      <AddMemberModal open={addModalOpen} onOpenChange={setAddModalOpen} />
    </>
  );
};

export default MemberListPage;
